import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Mail, XCircle } from "lucide-react";
import { API } from "../lib/api";
import { DiamondMark } from "../components/Brand";

export const Shell = ({ children }) => (
  <main className="min-h-screen bg-[#09040E] text-white flex items-center justify-center px-5 relative overflow-hidden">
    <div className="absolute w-[36rem] h-[36rem] rounded-full bg-violet-700/25 blur-[140px]" />
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
      className="relative w-full max-w-lg border border-gold/30 bg-[#100719]/90 backdrop-blur-xl p-8 sm:p-10 text-center">
      <img src="/img/logo.webp" alt="SeedoSellz" className="w-20 h-20 rounded-full mx-auto mb-6" />
      {children}
      <Link to="/" className="mt-8 inline-block text-xs tracking-[0.3em] uppercase text-violet-300 hover:text-gold" data-testid="back-home-link">← Back to the vault</Link>
    </motion.div>
  </main>
);

export function PaymentCancel() {
  return (
    <Shell>
      <XCircle className="w-8 h-8 text-violet-300 mx-auto" />
      <h1 className="mt-4 font-display font-black uppercase text-3xl" data-testid="payment-cancel-title">Checkout cancelled</h1>
      <p className="mt-3 text-sm text-violet-200/80">No charge was made. Your vault is still waiting whenever you're ready.</p>
    </Shell>
  );
}

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return undefined; }
    let tries = 0;
    let timer;
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data?.payment_status === "paid") {
          const res = await axios.get(`${API}/orders/${sessionId}`);
          if (alive) { setOrder(res.data ?? null); setState("paid"); }
          return;
        }
        if (data?.status === "expired") { if (alive) setState("error"); return; }
      } catch {
        if (tries > 3 && alive) { setState("error"); return; }
      }
      tries += 1;
      if (tries < 15) timer = setTimeout(poll, 2000);
      else if (alive) setState("error");
    };
    poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [sessionId]);

  if (state === "checking") return (
    <Shell>
      <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto" />
      <h1 className="mt-4 font-display font-black uppercase text-2xl" data-testid="payment-checking">Confirming payment…</h1>
    </Shell>
  );
  if (state === "error") return (
    <Shell>
      <h1 className="font-display font-black uppercase text-2xl" data-testid="payment-error">We couldn't confirm this payment</h1>
      <p className="mt-3 text-sm text-violet-200/80">If you were charged, DM us on Instagram @seedosellz with your receipt.</p>
    </Shell>
  );
  return (
    <Shell>
      <p className="text-[11px] tracking-[0.4em] uppercase text-gold flex items-center justify-center gap-2"><DiamondMark className="w-3 h-3" /> Access granted</p>
      <h1 className="mt-4 font-display font-black uppercase text-3xl leading-tight" data-testid="payment-success-title">Your vault is unlocked</h1>
      <p className="mt-3 text-violet-100" data-testid="payment-success-product">{order?.product_name ?? "Your purchase"}</p>
      <Link to={order?.access_path ?? "/"} className="btn-gold mt-8 w-full py-4 text-xs" data-testid="open-access-page-btn">
        Open my access page <ArrowRight className="w-4 h-4 ml-2" />
      </Link>
      <p className="mt-5 text-xs text-violet-300">Bookmark it: this link is private to you.</p>
      <p className="mt-5 text-xs text-violet-300 flex items-center justify-center gap-2" data-testid="email-delivery-note">
        <Mail className="w-3.5 h-3.5" />
        {order?.email ? `A copy is on its way to ${order.email}` : "A copy will be sent to your checkout email"}
      </p>
    </Shell>
  );
}
